import { useTranslation } from 'react-i18next';

import AttendancePendenciesFilterBar from '@/components/shared/pendencies/AttendancePendenciesFilterBar';
import AttendancePendenciesTable from '@/components/shared/pendencies/tables/AttendancePendenciesTable';
import ResultsCount from '@/components/shared/ResultsCount';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { PaginationControl } from '@/components/ui/daisy';
import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import useAuth from '@/data/hook/useAuth';
import useListService from '@/data/hook/useListService';
import usePageErrorReset from '@/data/hook/shared/usePageErrorReset';
import usePagination from '@/data/hook/shared/usePagination';
import usePendenciesFilter from '@/data/hook/usePendenciesFilter';
import { scheduledLessonsQueryKeys } from '@/data/services/querykeys';

export default function AttendancePendenciesTab() {
  const { t } = useTranslation('translation', { keyPrefix: 'pendencies' });
  const { user } = useAuth();

  const {
    filters,
    mainFilters,
    advancedFilters,
    advancedFiltersCount,
    toggleOrdering,
    updateMainFilters,
    updateAdvancedFilters,
    clearAdvancedFilters,
  } = usePendenciesFilter();
  const { pageNumber, pageSize, firstPage, setPageNumber } = usePagination();

  const { queryKey, queryFn } = scheduledLessonsQueryKeys.list({
    ...filters,
    pageNumber,
    pageSize,
  });

  const {
    results: scheduledLessons,
    meta,
    isInitialLoading,
    error,
  } = useListService({
    queryKey,
    queryFn,
    enabled: !!user,
    keepPreviousData: true,
    staleTime,
  });

  const currentPage = pageNumber;
  const totalPages = meta?.pagination.pages ?? 1;
  const totalResults = meta?.pagination.count ?? 0;

  usePageErrorReset({ error, pageNumber, firstPage, setPageNumber });

  return (
    <div className="flex flex-col gap-6">
      <AttendancePendenciesFilterBar
        mainFilters={mainFilters}
        advancedFilters={advancedFilters}
        advancedFiltersCount={advancedFiltersCount}
        updateMainFilters={updateMainFilters}
        updateAdvancedFilters={updateAdvancedFilters}
        clearAdvancedFilters={clearAdvancedFilters}
      />

      <ResultsCount
        className="self-end"
        pageNumber={pageNumber}
        pageSize={pageSize}
        total={totalResults}
      />

      <AttendancePendenciesTable
        ordering={filters.ordering}
        onToggleOrdering={toggleOrdering}
        data={scheduledLessons}
        isLoading={isInitialLoading}
        error={error}
        emptyMessage={t('empty')}
        classNames={{
          container: 'rounded-2xl border border-base-content/5 bg-base-200',
          header: 'text-xs',
        }}
      />

      <ConditionalRenderer condition={totalPages > 1}>
        <PaginationControl
          className="self-end"
          color="primary"
          variant="soft"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setPageNumber}
          showPrevNext={false}
        />
      </ConditionalRenderer>
    </div>
  );
}
